import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { Button } from '@mote/ui'
import { Content } from '../../layouts/shared/content'
import { useMediaQuery } from '@mote/helpers'
import { CompareProducts } from './compare.products'
import { ProductData } from './product.data'
import { useProductsQuery, useTrackPurchaseFlow } from '@mote/api'

import { CenteredRoot, H1, H2, LeftP, Promoted } from './styles.common'

import {
  PageTitle,
  Products,
  Product,
  ProductTop,
  ProductMiddle,
  ProductBottom,
  ImageContainer,
  Divider,
  ProductSelectors,
  ProductButtonSelected,
  ProductButtonUnselected,
  PricingTierTitle
} from './styles.pricing.page.jsx'
import { FreeTierPricingSection } from './pricing-sections/FreeTierPricingSection'
import { UnlimitedTierPricingSection } from './pricing-sections/UnlimitedTierPricingSection'
import { SchoolsTierPricingSection } from './pricing-sections/SchoolsTierPricingSection'

export const PricingPageContent = ({ className, comparisonOpen = true }) => {
  const history = useHistory()
  const [products, setProducts] = useState(new ProductData(null, {}))
  const { mutate: trackPurchaseFlow } = useTrackPurchaseFlow()
  const {
    data: rawProducts,
    loading: isRawProductsLoading,
    error: rawProductsError
  } = useProductsQuery()
  const mediaBreakPoint = useMediaQuery([640, 992])
  const [highlightedProduct, setHighlightedProduct] = useState('Unlimited')

  useEffect(() => {
    trackPurchaseFlow({
      productEnum: 'all',
      step: 1,
      stage: 'Loading pricing',
      hasStart: true
    })
  }, [])

  useEffect(() => {
    if (isRawProductsLoading || Object.keys(products.data).length > 0) return

    if (rawProductsError || !rawProducts) {
      trackPurchaseFlow({
        productEnum: 'all',
        step: 2,
        stage: "Couldn't fetch products pricing data for pricing page",
        hasFailed: true
      })
      return
    }

    const newProducts = new ProductData(true, rawProducts)
    setProducts(newProducts)

    trackPurchaseFlow({
      productEnum: 'all',
      step: 2,
      stage: 'Pricing page loaded'
    })
  }, [products, isRawProductsLoading, rawProductsError, rawProducts])

  function canShowProduct(productName) {
    return mediaBreakPoint !== 640 || productName === highlightedProduct
  }

  function ProductSelector(props) {
    // console.log('Called with props: ', props)
    if (canShowProduct(props.text)) {
      return (
        <ProductButtonSelected
          onClick={() => setHighlightedProduct(props.text)}
        >
          {props.text}{' '}
        </ProductButtonSelected>
      )
    } else {
      return (
        <ProductButtonUnselected
          onClick={() => setHighlightedProduct(props.text)}
        >
          {props.text}
        </ProductButtonUnselected>
      )
    }
  }

  return (
    <CenteredRoot className={className}>
      <Content>
        <PageTitle>
          <H1>There's a Mote for everyone</H1>
        </PageTitle>
        {mediaBreakPoint === 640 && (
          <ProductSelectors>
            <ProductSelector text='Free' />
            <ProductSelector text='Unlimited' />
            <ProductSelector text='Schools' />
          </ProductSelectors>
        )}
        <Products>
          {canShowProduct('Free') && (
            <Product>
              <ProductTop>
                <ImageContainer backgroundColor='#FBF3FD'>
                  <span>💜</span>
                </ImageContainer>
                <PricingTierTitle>Free!</PricingTierTitle>
                For those starting out
                <Divider />
              </ProductTop>
              <ProductMiddle>
                Record a mote on any webpage
                <br />
                Save time with app integrations
                <br />
                30 second recording time
                <br />
                20 motes per month limit
              </ProductMiddle>
              <ProductBottom>
                <FreeTierPricingSection products={products} />
              </ProductBottom>
            </Product>
          )}

          {canShowProduct('Unlimited') && (
            <Product>
              <ProductTop>
                <ImageContainer backgroundColor='rgba(240, 153, 89, 0.1)'>
                  <span>🏆</span>
                </ImageContainer>
                <PricingTierTitle>Unlimited</PricingTierTitle>
                For individuals and teams
                <Divider />
              </ProductTop>

              <ProductMiddle>
                <Promoted>Everything in Free +</Promoted>
                180 second recording time
                <br />
                Voice to text transcription <br />
                Save and re-use motes
              </ProductMiddle>

              <ProductBottom>
                <UnlimitedTierPricingSection products={products} />
              </ProductBottom>
            </Product>
          )}
          {canShowProduct('Schools') && (
            <Product>
              <ProductTop>
                <ImageContainer backgroundColor='rgba(133, 184, 251, 0.1)'>
                  <span>🏫</span>
                </ImageContainer>
                <PricingTierTitle>Schools</PricingTierTitle>
                For entire schools and districts
                <Divider />
              </ProductTop>
              <ProductMiddle>
                <Promoted>Everything in Unlimited +</Promoted>
                Huge bulk discounts (up to 97%) <br />
                Domain enablement <br />
                Custom PD and onboarding
              </ProductMiddle>

              <ProductBottom>
                <SchoolsTierPricingSection />
              </ProductBottom>
            </Product>
          )}
        </Products>

        <H2>
          {!comparisonOpen
            ? "Can't decide which plan to choose?"
            : 'Choose the right plan for you'}
        </H2>

        <LeftP />
        {!comparisonOpen && (
          <Button
            text='Compare full features'
            variant='primary-inverted'
            onClick={() => {
              trackPurchaseFlow({
                productEnum: 'all',
                step: 3,
                stage: 'Compare Products Clicked'
              })
              history.push('/pricing')
            }}
          />
        )}

        {comparisonOpen && <CompareProducts products={products} />}
      </Content>
    </CenteredRoot>
  )
}
